import React from "react";
import { useLocation } from "react-router-dom";
import SEO from "../../seo-routes.json";
import { heroSrcSet } from "../../mock";
import {
  Compass, ShieldCheck, Car, Plane, Star, Clock, User, Radar, Luggage, Anchor, Map, Tag, Baby, Wine,
  HeartHandshake,
} from "lucide-react";

const MAP = {
  compass: Compass, shield: ShieldCheck, car: Car, plane: Plane, star: Star, clock: Clock, user: User,
  radar: Radar, luggage: Luggage, anchor: Anchor, map: Map, tag: Tag, baby: Baby, glass: Wine, heart: HeartHandshake,
};

export const Icon = ({ name, className }) => {
  const C = MAP[name] || Star;
  return <C className={className} />;
};

export const IconTile = ({ name, dark = false }) => (
  <span className={`inline-flex shrink-0 w-10 h-10 sm:w-11 sm:h-11 items-center justify-center rounded-lg ${dark ? "bg-[#183C35] text-[#F7F3EA]" : "bg-[#E4EFF0] text-[#3E7F8E]"}`}>
    <Icon name={name} className="w-[18px] h-[18px]" />
  </span>
);

export const FeatureCard = ({ icon, title, text, dark, testid }) => (
  <div className="reveal lift bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6 flex gap-4 sm:block" data-testid={testid}>
    <IconTile name={icon} dark={dark} />
    <div>
      <h3 className="sm:mt-5 text-[16px] sm:text-[19px] font-medium text-[#183C35]">{title}</h3>
      <p className="mt-1 sm:mt-2.5 text-[14px] sm:text-[15px] leading-relaxed text-[#202522]/70">{text}</p>
    </div>
  </div>
);

export const SectionHead = ({ eyebrow, title, sub, center = false }) => (
  <div className={`reveal ${center ? "text-center" : ""}`}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="font-serif-d text-[26px] sm:text-[40px] font-semibold text-[#183C35] mt-2 sm:mt-3 leading-tight">{title}</h2>
    {sub && <p className={`mt-2 sm:mt-3 text-[15px] sm:text-[17px] text-[#202522]/75 ${center ? "mx-auto max-w-xl" : ""}`}>{sub}</p>}
  </div>
);

const SeoH1 = ({ title }) => {
  const seoH1 = SEO.pages[useLocation().pathname]?.h1;
  return seoH1 && seoH1 !== title ? <span className="sr-only">{seoH1} – </span> : null;
};

export const PageHero = ({ image, eyebrow, title, sub, children, tall = false }) => (
  <section className={`relative overflow-hidden ${tall ? "min-h-[380px] sm:min-h-[560px]" : "min-h-[260px] sm:min-h-[440px]"} flex items-center`}>
    <img src={image} srcSet={heroSrcSet(image)} sizes="100vw" fetchPriority="high" alt={`${title} – Aloha Corfu Transfers, Corfu private transfers and tours`} className="absolute inset-0 w-full h-full object-cover scale-105" />
    <div className="absolute inset-0 bg-[#183C35]/55" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#183C35]/60 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-6 w-full py-10 sm:py-20 fade-up">
      <p className="text-[11px] sm:text-[13px] tracking-[0.22em] uppercase text-[#F7F3EA]/85">{eyebrow}</p>
      <h1 className="font-serif-d font-bold text-[32px] sm:text-[60px] leading-[1.08] text-[#F7F3EA] mt-3 sm:mt-4 max-w-3xl"><SeoH1 title={title} />{title}</h1>
      {sub && <p className="mt-3 sm:mt-5 text-[15px] sm:text-[18px] text-[#F7F3EA]/85 max-w-xl leading-relaxed">{sub}</p>}
      {children}
    </div>
  </section>
);
